import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, MicOffIcon, VolumeIcon, Volume2Icon, XIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
interface VoiceControlProps {
  isListening: boolean;
  onToggle: () => void;
}
const voiceCommands = [{
  command: '"Lights to 50%"',
  action: 'Adjust lighting'
}, {
  command: '"Close curtains"',
  action: 'Control blinds'
}, {
  command: '"Set temperature to 22"',
  action: 'Climate control'
}, {
  command: '"Privacy mode on"',
  action: 'Activate privacy glass'
}, {
  command: '"Call butler"',
  action: 'Request assistance'
}, {
  command: '"Start presentation"',
  action: 'Launch slides'
}];
export function VoiceControl({
  isListening,
  onToggle
}: VoiceControlProps) {
  const [transcript, setTranscript] = useState('');
  const [status, setStatus] = useState<'idle' | 'listening' | 'processing' | 'success' | 'error'>('idle');
  useEffect(() => {
    if (isListening) {
      setStatus('listening');
      // Simulate voice recognition
      const timer1 = setTimeout(() => {
        setTranscript('Set lights to 75 percent');
      }, 2000);
      const timer2 = setTimeout(() => {
        setStatus('processing');
      }, 3000);
      const timer3 = setTimeout(() => {
        setStatus('success');
      }, 4000);
      const timer4 = setTimeout(() => {
        setStatus('idle');
        setTranscript('');
        onToggle();
      }, 6000);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    } else {
      setStatus('idle');
      setTranscript('');
    }
  }, [isListening, onToggle]);
  return <>
      {/* Voice Button (always visible) */}
      <motion.button whileTap={{
      scale: 0.95
    }} onClick={onToggle} className={`fixed bottom-8 left-8 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transition-all z-40 ${isListening ? 'bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] shadow-[rgba(201,168,76,0.4)]' : 'glass-card hover:bg-[rgba(255,255,255,0.08)]'}`} data-id="element-624">
        {isListening ? <MicIcon className="w-7 h-7 text-[#0D0F14]" data-id="element-625" /> : <MicOffIcon className="w-7 h-7 text-gray-400" data-id="element-626" />}

        {/* Pulse rings when listening */}
        {isListening && <>
            <motion.div animate={{
          scale: [1, 1.5],
          opacity: [0.5, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity
        }} className="absolute inset-0 rounded-2xl bg-[#C9A84C]" data-id="element-627" />
            <motion.div animate={{
          scale: [1, 1.8],
          opacity: [0.3, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          delay: 0.3
        }} className="absolute inset-0 rounded-2xl bg-[#C9A84C]" data-id="element-628" />
          </>}
      </motion.button>

      {/* Voice Overlay */}
      <AnimatePresence data-id="element-629">
        {isListening && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center" data-id="element-630">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="text-center max-w-2xl px-8" data-id="element-631">
              {/* Close button */}
              <button onClick={onToggle} className="absolute top-8 right-8 w-12 h-12 rounded-xl bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] flex items-center justify-center transition-colors" data-id="element-632">
                <XIcon className="w-6 h-6 text-white" data-id="element-633" />
              </button>

              {/* Animated microphone */}
              <div className="relative mb-8" data-id="element-634">
                <motion.div animate={status === 'listening' ? {
              scale: [1, 1.1, 1]
            } : {}} transition={{
              duration: 1,
              repeat: Infinity
            }} className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto ${status === 'success' ? 'bg-[rgba(34,197,94,0.2)] border-2 border-green-500' : status === 'error' ? 'bg-[rgba(239,68,68,0.2)] border-2 border-red-500' : 'bg-gradient-to-br from-[#C9A84C] to-[#D4AF37]'}`} data-id="element-635">
                  {status === 'success' ? <CheckCircle2Icon className="w-16 h-16 text-green-500" data-id="element-636" /> : status === 'error' ? <AlertCircleIcon className="w-16 h-16 text-red-500" data-id="element-637" /> : <MicIcon className="w-16 h-16 text-[#0D0F14]" data-id="element-638" />}
                </motion.div>

                {/* Sound wave visualization */}
                {status === 'listening' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-639">
                    {[...Array(3)].map((_, i) => <motion.div key={i} animate={{
                scale: [1, 2, 1],
                opacity: [0.4, 0, 0.4]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.4
              }} className="absolute w-32 h-32 rounded-full border border-[#C9A84C]" data-id="element-640" />)}
                  </div>}
              </div>

              {/* Status text */}
              <h2 className="text-3xl font-['Playfair_Display'] text-white mb-4" data-id="element-641">
                {status === 'listening' && 'Listening...'}
                {status === 'processing' && 'Processing...'}
                {status === 'success' && 'Command Executed'}
                {status === 'error' && 'Command Not Recognized'}
              </h2>

              {/* Transcript */}
              {transcript && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-8" data-id="element-642">
                  <p className="text-xl text-[#C9A84C] font-medium" data-id="element-643">
                    "{transcript}"
                  </p>
                </motion.div>}

              {/* Voice commands hint */}
              {status === 'listening' && !transcript && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="mt-8" data-id="element-644">
                  <p className="text-sm text-gray-500 mb-4" data-id="element-645">Try saying:</p>
                  <div className="grid grid-cols-2 gap-3" data-id="element-646">
                    {voiceCommands.slice(0, 4).map((cmd, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6 + index * 0.1
              }} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.05)]" data-id="element-647">
                        <Volume2Icon className="w-4 h-4 text-[#C9A84C]" data-id="element-648" />
                        <div className="text-left" data-id="element-649">
                          <p className="text-sm text-white" data-id="element-650">{cmd.command}</p>
                          <p className="text-xs text-gray-500" data-id="element-651">{cmd.action}</p>
                        </div>
                      </motion.div>)}
                  </div>
                </motion.div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}